"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { LuLoader2 } from "react-icons/lu";
import { AttachmentPreviewImage } from "@/components/scribe/attachment-preview-image";
import { Button, Checkbox, Label } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { NoteAttachment, NoteAttachmentTag } from "@/types/notes";
import { useTagMutations } from "@/api/provider/scribe/note-mutation";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface BulkTagAttachmentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachments: NoteAttachment[];
  onComplete?: () => void;
}
export default function BulkTagAttachmentsModal(t0) {
  const $ = _c(59);
  const {
    open,
    onOpenChange,
    attachments,
    onComplete
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = noteQueryOptions.tags();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: t2
  } = useQuery(t1);
  let t3;
  if ($[1] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const allTags = t3;
  const {
    bulkAssignTagsMutation
  } = useTagMutations();
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Set();
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [selectedTagIds, setSelectedTagIds] = useState(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = tagId => {
      setSelectedTagIds(prev => {
        const next = new Set(prev);
        if (next.has(tagId)) {
          next.delete(tagId);
        } else {
          next.add(tagId);
        }
        return next;
      });
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleToggleTag = t5;
  let t6;
  if ($[5] !== attachments || $[6] !== bulkAssignTagsMutation || $[7] !== onComplete || $[8] !== onOpenChange || $[9] !== selectedTagIds) {
    t6 = () => {
      if (selectedTagIds.size === 0) {
        toast({
          title: "No tags selected",
          description: "Select at least one tag to assign.",
          variant: "info"
        });
        return;
      }
      bulkAssignTagsMutation.mutate({
        attachmentIds: attachments.map(_temp),
        tagIds: Array.from(selectedTagIds)
      }, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: `Assigned ${selectedTagIds.size} tag${selectedTagIds.size !== 1 ? "s" : ""} to ${attachments.length} photo${attachments.length !== 1 ? "s" : ""}`,
            variant: "success"
          });
          setSelectedTagIds(new Set());
          onOpenChange(false);
          onComplete?.();
        }
      });
    };
    $[5] = attachments;
    $[6] = bulkAssignTagsMutation;
    $[7] = onComplete;
    $[8] = onOpenChange;
    $[9] = selectedTagIds;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleSave = t6;
  let t7;
  bb0: {
    if (attachments.length === 0) {
      let t8;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = new Set();
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[12] !== attachments) {
      const tagSets = attachments.map(_temp3);
      const first = tagSets[0];
      t8 = new Set(Array.from(first).filter(id => tagSets.every(s => s.has(id))));
      $[12] = attachments;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    t7 = t8;
  }
  const commonTagIds = t7;
  let t8;
  if ($[14] !== onOpenChange) {
    t8 = val => {
      if (!val) {
        setSelectedTagIds(new Set());
      }
      onOpenChange(val);
    };
    $[14] = onOpenChange;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogTitle>Assign Tags</DialogTitle>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== attachments.length) {
    t10 = attachments.length !== 1 ? <span>{"s"}</span> : <span>{""}</span>;
    $[17] = attachments.length;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== attachments.length || $[20] !== t10) {
    t11 = <DialogHeader>{t9}<DialogDescription>Add tags to {attachments.length} selected photo{t10}</DialogDescription></DialogHeader>;
    $[19] = attachments.length;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== attachments) {
    t12 = attachments.slice(0, 8).map(_temp4);
    $[22] = attachments;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== attachments.length) {
    t13 = attachments.length > 8 && <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border bg-muted text-caption text-foreground-secondary">+{attachments.length - 8}</div>;
    $[24] = attachments.length;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <div className="mb-4 flex flex-wrap gap-2">{t12}{t13}</div>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== allTags || $[30] !== commonTagIds || $[31] !== selectedTagIds) {
    t15 = allTags.length > 0 ? <div className="space-y-1"><Label className="text-caption text-foreground-secondary">Select tags to add</Label><div className="space-y-1 rounded-lg border border-border p-2">{allTags.map(tag => {
          const isSelected = selectedTagIds.has(tag.id);
          const isCommon = commonTagIds.has(tag.id);
          return <label key={tag.id} className={cx("flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-hover", isSelected && "bg-hover")}><Checkbox checked={isSelected} onCheckedChange={() => handleToggleTag(tag.id)} /><div className="h-3 w-3 rounded-full border border-border" style={{
              backgroundColor: tag.color
            }} /><span className="text-body">{tag.name}</span>{isCommon && <span className="ml-auto text-caption text-foreground-secondary">already on all</span>}</label>;
        })}</div></div> : <p className="py-4 text-center text-body text-foreground-secondary">No tags available. Create tags in Settings first.</p>;
    $[29] = allTags;
    $[30] = commonTagIds;
    $[31] = selectedTagIds;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <DialogBody className="max-h-[60vh] overflow-y-auto">{t14}{t15}</DialogBody>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== onOpenChange) {
    t17 = () => {
      setSelectedTagIds(new Set());
      onOpenChange(false);
    };
    $[36] = onOpenChange;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== bulkAssignTagsMutation.isPending || $[39] !== t17) {
    t18 = <Button variant="secondary" onClick={t17} disabled={bulkAssignTagsMutation.isPending}>Cancel</Button>;
    $[38] = bulkAssignTagsMutation.isPending;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const t19 = bulkAssignTagsMutation.isPending || selectedTagIds.size === 0;
  let t20;
  if ($[41] !== bulkAssignTagsMutation.isPending || $[42] !== selectedTagIds.size) {
    t20 = bulkAssignTagsMutation.isPending ? <><LuLoader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</> : `Assign${selectedTagIds.size > 0 ? ` (${selectedTagIds.size})` : ""}`;
    $[41] = bulkAssignTagsMutation.isPending;
    $[42] = selectedTagIds.size;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== handleSave || $[45] !== t19 || $[46] !== t20) {
    t21 = <Button onClick={handleSave} disabled={t19}>{t20}</Button>;
    $[44] = handleSave;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t18 || $[49] !== t21) {
    t22 = <DialogFooter>{t18}{t21}</DialogFooter>;
    $[48] = t18;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t11 || $[52] !== t16 || $[53] !== t22) {
    t23 = <DialogContent className="max-w-lg">{t11}{t16}{t22}</DialogContent>;
    $[51] = t11;
    $[52] = t16;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== open || $[56] !== t23 || $[57] !== t8) {
    t24 = <Dialog open={open} onOpenChange={t8}>{t23}</Dialog>;
    $[55] = open;
    $[56] = t23;
    $[57] = t8;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
}
function _temp4(att) {
  return <div key={att.id} className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border bg-muted"><AttachmentPreviewImage thumbnailSignedUrl={att.thumbnailSignedUrl} signedUrl={att.signedUrl} alt={att.name || "Photo"} iconClassName="h-4 w-4" /></div>;
}
function _temp3(a_0) {
  return new Set((a_0.tags ?? []).map(_temp2));
}
function _temp2(t) {
  return t.id;
}
function _temp(a) {
  return a.id;
}